import React from 'react';
import { Col, Colors, Row, Text } from 'src/common';
import {
  ApplicationActionType,
  ApplicationStep,
  ButtonConfig,
  ConfirmModal,
  Modal,
} from 'src/features/common';
import { ApplicationContext } from 'src/features/order/context';
import {
  ButtonParamType,
  DefaultButtonStyle,
} from 'src/features/common/components/button/revamp-button';
import ButtonGroup, {
  LayoutType,
} from 'src/features/common/components/button-group/revamp-button-group';
import { Toast } from 'src/features/common/components/toast/toast';
import { Environment } from 'src/Environment';
import { getDeliveryAddressList } from 'src/features/common/helpers/delivery-options';
import { AppDelivery } from 'src/features/common/types/application/delivery';
import { yourDetailsRoute } from 'src/features/order/helpers/routes';

interface Address {
  addressLines: string[];
  assignedDevices?: boolean;
  onDeleteButtonClicked: () => void;
  onEditButtonClicked: () => void;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  onAddAddress: () => void;
  onEditButtonClicked: (addressId?: string) => void;
}

const MODAL_TEXT = {
  Heading: 'Are you sure?',
  Subtext: 'You are about to delete an address:',
  DefaultAddressSubtext:
    "You can't delete a default address with assigned devices, please choose a new default address.",
};

const hasDevicesAssigned = (deliveries: AppDelivery[], addressId: string): boolean => {
  return deliveries.some((delivery) => delivery.addressId === addressId);
};

export const DeliveryAddressListModal = (props: Props) => {
  const { application, dispatch } = React.useContext(ApplicationContext);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [addressToBeDeleted, setAddressToBeDeleted] = React.useState('');
  const [confirmModalHeading, setConfirmModalHeading] = React.useState(MODAL_TEXT.Heading);
  const [confirmModalSubtext, setConfirmModalSubtext] = React.useState(MODAL_TEXT.Subtext);
  const [indexOfDeleteAddress, setIndexOfDeleteAddress] = React.useState<number>(-1);
  const [addressIdToDelete, setAddressIdToDelete] = React.useState('');

  const buttonContinue: ButtonConfig = {
    automationId: 'Revamp_My_Address_LIst_Button',
    label: 'Add a new address',
    buttonStyle: DefaultButtonStyle().hasPrimaryAction().isLargeSize().hasLightMode().build(),
    className: 'add-new-address-button',
    buttonParams: {
      type: ButtonParamType.NormalButton,
      onClick: () => props.onAddAddress(),
    },
  };

  const addresses: Address[] = React.useMemo(() => {
    return getDeliveryAddressList(
      application,
      (addressId: string, address: string[]) => {
        if (
          addressId === 'default' &&
          hasDevicesAssigned(application.deliveries || [], addressId)
        ) {
          setConfirmModalHeading('');
          setConfirmModalSubtext(MODAL_TEXT.DefaultAddressSubtext);
          props.onClose();
          setShowConfirmModal(true);
          return;
        }

        props.onClose();
        setShowConfirmModal(true);
        setAddressToBeDeleted(address.join(', '));
        setAddressIdToDelete(addressId);
      },
      (addressId: string) => {
        // on edit address
        if (addressId === 'default') {
          const yourDetailsStep: ApplicationStep | undefined = application.steps?.find(
            (step) => step.route === yourDetailsRoute,
          );

          if (!yourDetailsStep) {
            throw new Error(
              `Delivery Address List revamp: step name not supported: ${yourDetailsRoute} not found`,
            );
          }

          dispatch({
            type: ApplicationActionType.QUICK_EDIT,
            steps: [yourDetailsStep],
          });

          return;
        }

        props.onEditButtonClicked(addressId);
        props.onClose();
      },
    );
  }, [application.addresses]);

  return (
    <>
      <ConfirmModal
        visible={showConfirmModal}
        heading={confirmModalHeading}
        showCloseButton={true}
        cancelButtonLabel="Cancel"
        confirmButtonLabel="Delete"
        onCancel={() => {
          setShowConfirmModal(false);
          setIndexOfDeleteAddress(-1);
          setAddressToBeDeleted('');
          setConfirmModalHeading(MODAL_TEXT.Heading);
          setConfirmModalSubtext(MODAL_TEXT.Subtext);
        }}
        onConfirm={
          confirmModalSubtext === MODAL_TEXT.DefaultAddressSubtext
            ? undefined
            : () => {
                if (!Environment.current.disabledFeatures.dpo) {
                  dispatch({
                    type: ApplicationActionType.DELETE_ADDRESS,
                    addressId: addressIdToDelete,
                  });

                  setShowConfirmModal(false);
                  setAddressIdToDelete('');
                  setAddressToBeDeleted('');
                  setConfirmModalHeading(MODAL_TEXT.Heading);
                  setConfirmModalSubtext(MODAL_TEXT.Subtext);
                  return;
                }

                if (indexOfDeleteAddress >= 0) {
                  const newDeliveries = [...(application.opportunity?.deliveries || [])];
                  newDeliveries.splice(indexOfDeleteAddress, 1);
                  dispatch({
                    type: ApplicationActionType.UPDATE_DELIVERIES,
                    deliveries: newDeliveries,
                  });

                  setShowConfirmModal(false);
                  setIndexOfDeleteAddress(-1);
                  setAddressToBeDeleted('');
                  setConfirmModalHeading(MODAL_TEXT.Heading);
                  setConfirmModalSubtext(MODAL_TEXT.Subtext);

                  return;
                }
              }
        }
      >
        {addressToBeDeleted && (
          <Col alignItems={'center'} margin={'20px 0'}>
            <Text as={'p'}>{MODAL_TEXT.Subtext}</Text>
            <Text fontWeight={700} fontSize={'16px'} as={'p'}>
              {addressToBeDeleted}
            </Text>
          </Col>
        )}

        {confirmModalSubtext && !addressToBeDeleted && (
          <Text textAlign={'center'}>{confirmModalSubtext}</Text>
        )}
      </ConfirmModal>
      <Col
        css={`
          article.address-modal-overlay > section.modal-box {
            display: block;
          }

          section.scroll-box {
            align-items: flex-start !important;
            height: 80vh;
          }
        `}
      >
        <Modal size="medium" onClose={() => props.onClose()} visible={props.visible}>
          <Col overflowY={'auto'} flex={1} width={'100%'}>
            <Toast />
            <Text margin={'30px 0'} fontWeight={600} textAlign={'center'} fontSize={'22px'}>
              My address list
            </Text>

            <Col gap={'5px'}>
              {addresses.map((address: Address, idx: number) => (
                <Row padding={'12px 20px'} backgroundColor={Colors.Grey25} key={idx}>
                  <Text
                    flex="1"
                    margin={'0 12px 0 0'}
                    overflow={'hidden'}
                    textOverflow={'ellipsis'}
                    whiteSpace={'nowrap'}
                    fontSize={'14px'}
                    fontWeight={500}
                  >
                    {address.addressLines.join(', ')}
                  </Text>
                  <Row gap={'9px'}>
                    <Text
                      textDecoration={'underline'}
                      cursor={'pointer'}
                      onClick={() => address.onEditButtonClicked()}
                      fontSize={'16px'}
                    >
                      Edit
                    </Text>
                    |
                    <Text
                      textDecoration={'underline'}
                      cursor={'pointer'}
                      onClick={() => address.onDeleteButtonClicked()}
                      fontSize={'16px'}
                    >
                      Delete
                    </Text>
                  </Row>
                </Row>
              ))}
            </Col>
          </Col>
          <Col padding={'0 20px'}>
            <Text justifyContent={'center'} fontSize={'14px'} as={'p'}>
              <Text fontWeight={'bold'} display={'inline-block'} as={'span'}>
                Please note:
              </Text>{' '}
              Deliveries can only be made to a business or operating address, deliveries are made
              Monday to Friday between 8am to 5pm.
            </Text>

            <Col
              css={`
                .add-new-address-button {
                  width: 260px;
                  margin-top: 60px;
                }
              `}
            >
              <ButtonGroup buttons={[buttonContinue]} layoutType={LayoutType.CENTERED} />
            </Col>
          </Col>
        </Modal>
      </Col>
    </>
  );
};
